import { fetchConfig } from '@theme/utilities';

/**
 * Hidden cart/order attribute read by the order confirmation notification.
 * Snake_case is required at the Shopify cart and notification Liquid boundary
 * (`attributes._sis_logo_url`). Emails cannot unwrap shop_in_shop metaobjects.
 */
export const SIS_EMAIL_LOGO_ATTRIBUTE = '_sis_logo_url';

const SESSION_STORAGE_KEY = 'sisEmailLogoUrl';

/**
 * Builds the cart.js update payload that stamps the SIS logo CDN URL on the cart.
 *
 * @param {string} logoUrl - Public image URL from the shop_in_shop logo file.
 * @returns {string} JSON body for POST to cart_update_url
 */
export function buildCartAttributeUpdateBody(logoUrl) {
  return JSON.stringify({
    attributes: {
      [SIS_EMAIL_LOGO_ATTRIBUTE]: logoUrl,
    },
  });
}

/**
 * Skip design mode, blank URLs, and repeat writes of the same URL.
 *
 * @param {string} logoUrl
 * @param {string | null} storedUrl
 * @param {boolean} isDesignMode
 * @returns {boolean}
 */
export function shouldSyncSisEmailLogo(logoUrl, storedUrl, isDesignMode) {
  if (isDesignMode) return false;
  if (logoUrl === '') return false;
  return logoUrl !== storedUrl;
}

/**
 * Reads `data-sis-logo-url` from body (set in layout when SIS + logo exist)
 * and copies it to cart attribute `_sis_logo_url` for the confirmation email.
 */
export async function syncSisEmailLogo() {
  const logoUrl = document.body.dataset.sisLogoUrl?.trim() ?? '';
  const isDesignMode = Boolean(window.Shopify?.designMode);

  let storedUrl = null;
  try {
    storedUrl = sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    storedUrl = null;
  }

  if (!shouldSyncSisEmailLogo(logoUrl, storedUrl, isDesignMode)) return;

  const cartUpdateUrl = Theme.routes.cart_update_url;
  if (!cartUpdateUrl) return;

  try {
    const response = await fetch(
      cartUpdateUrl,
      fetchConfig('json', {
        body: buildCartAttributeUpdateBody(logoUrl),
      })
    );

    if (!response.ok) return;

    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, logoUrl);
    } catch {
      // Private mode can block sessionStorage; the next SIS page view retries.
    }
  } catch {
    // Cart update can fail offline; retry on the next SIS page view.
  }
}

if (typeof document !== 'undefined') {
  syncSisEmailLogo();
}
